import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Activity, Crosshair, Map, ScrollText, ShieldAlert, Anchor, Radio, Lock, Eye,
  Zap, KeyRound, LogOut, Plug,
} from "lucide-react";
import api from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import PulseBell from "@/components/PulseBell";

const NAV = [
  { to: "/", label: "Command", icon: Activity, code: "I" },
  { to: "/hunts", label: "Threat Hunts", icon: Crosshair, code: "II" },
  { to: "/heatmaps", label: "Heatmaps", icon: Map, code: "III" },
  { to: "/incidents", label: "Incidents", icon: ShieldAlert, code: "IV" },
  { to: "/audit", label: "Audit & Governance", icon: ScrollText, code: "V" },
  { to: "/pulse", label: "Sentinel Pulse", icon: Zap, code: "VI" },
  { to: "/intake", label: "Intake Hull", icon: Plug, code: "VII" },
  { to: "/mfa", label: "MFA · Identity", icon: KeyRound, code: "VIII" },
];

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="font-mono text-[11px] tracking-[0.18em] text-ivory-2" data-testid="topbar-clock">
      {now.toISOString().replace("T", " ").slice(0, 19)}Z
    </span>
  );
};

const StatusOrb = () => {
  const [status, setStatus] = useState("checking");
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    let mounted = true;
    const ping = async () => {
      try {
        const { data } = await api.get("/health");
        if (mounted) { setStatus(data.status); setCounts(data.counts); }
      } catch { if (mounted) setStatus("degraded"); }
    };
    ping();
    const t = setInterval(ping, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, []);
  const ok = status === "operational";
  return (
    <div className="flex items-center gap-2" data-testid="health-orb">
      <span
        className="w-2 h-2 rounded-full"
        style={{ background: ok ? "#6cc88f" : "#ff7a6e", boxShadow: ok ? "0 0 12px #6cc88f" : "0 0 12px #ff7a6e" }}
      />
      <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-ivory-2">
        Vessel · {ok ? "Operational" : status}
      </span>
      {counts && (
        <span className="font-mono text-[10px] text-ivory-3 ml-2">
          E:{counts.events} A:{counts.alerts} I:{counts.incidents} H:{counts.hunts}
        </span>
      )}
    </div>
  );
};

const Layout = () => {
  const location = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const current = NAV.find((n) => n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to));
  const initials = (user?.name || "??").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex" data-testid="layout-root">
      {/* RAIL */}
      <aside className="nav-rail w-[260px] flex flex-col shrink-0 sticky top-0 h-screen" data-testid="nav-rail">
        <div className="p-6 pb-8 relative">
          <div className="flex items-center gap-3">
            <span className="sov-sigil" />
            <div>
              <div className="font-display text-[15px] tracking-[0.32em] text-gold-100 gold-glow">EIGOO</div>
              <div className="font-mono text-[9.5px] tracking-[0.4em] text-ivory-3 mt-0.5">TECH · OPSSA</div>
            </div>
          </div>
          <div className="gold-line mt-5" />
          <div className="font-serif italic text-[12px] text-ivory-3 mt-4 leading-snug">
            "Vigilance is the toll of sovereignty."
          </div>
        </div>

        <nav className="flex-1 flex flex-col gap-0.5 mt-2 overflow-y-auto" data-testid="nav-list">
          <div className="px-6 mb-2 font-mono text-[9px] tracking-[0.3em] text-ivory-3 uppercase">
            ─── Vessel Decks ───
          </div>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <span className="font-mono text-[9px] text-gold-300 w-7">{n.code}</span>
              <n.icon size={14} strokeWidth={1.5} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-5 border-t border-[color:var(--gold-line)]">
          <div className="flex items-center justify-between mb-3">
            <span className="font-mono text-[9px] tracking-[0.22em] text-ivory-3 uppercase">Operator</span>
            <Lock size={11} className={user?.mfa_enabled ? "text-[#6cc88f]" : "text-gold-300"} />
          </div>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 border border-[color:var(--gold-line)] flex items-center justify-center font-display text-[12px] text-gold-200 shrink-0">
              {initials}
            </div>
            <div className="leading-tight min-w-0 flex-1">
              <div className="font-serif text-[13px] text-ivory truncate">{user?.name || "—"}</div>
              <div className="font-mono text-[9px] text-ivory-3 tracking-[0.14em] uppercase truncate">
                {user?.rank || user?.role || "—"}
              </div>
            </div>
            <button onClick={handleLogout} className="text-ivory-3 hover:text-[#ff7a6e]" title="Disembark" data-testid="logout-btn">
              <LogOut size={13} />
            </button>
          </div>
          {!user?.mfa_enabled && user?.role === "admin" && (
            <button
              onClick={() => nav("/mfa")}
              className="mt-3 w-full text-left font-mono text-[9px] tracking-[0.18em] text-[#f5a73a] hover:text-gold-100 uppercase border border-[rgba(245,167,58,0.3)] px-2 py-1"
              data-testid="mfa-cta"
            >
              ⚠ Enrol MFA
            </button>
          )}
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 flex flex-col min-w-0">
        <div className="h-[64px] flex items-center justify-between px-6 border-b border-[color:var(--gold-line)] bg-[var(--obsidian-1)]/60 backdrop-blur sticky top-0 z-40" data-testid="topbar">
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-3">
              <Anchor size={14} className="text-gold-300" />
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-ivory-3">
                Deck {current?.code ?? "—"}
              </div>
              <div className="w-px h-4 bg-[color:var(--gold-line)]" />
              <div className="font-display text-[16px] tracking-[0.18em] text-gold-100">
                {current?.label?.toUpperCase() ?? "COMMAND"}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Radio size={12} className="text-gold-300" />
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-ivory-2">
                SIEM Stream · LIVE
              </span>
              <span className="dot-loader"><span /><span /><span /></span>
            </div>
            <div className="w-px h-5 bg-[color:var(--gold-line)]" />
            <Clock />
            <div className="w-px h-5 bg-[color:var(--gold-line)]" />
            <StatusOrb />
            <div className="w-px h-5 bg-[color:var(--gold-line)]" />
            <PulseBell />
            <div className="w-px h-5 bg-[color:var(--gold-line)]" />
            <div className="flex items-center gap-2">
              <Eye size={12} className="text-gold-300" />
              <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-ivory-2">
                CLASSIFIED · SOVEREIGN
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 p-8 fade-up" data-testid="page-content">
          <Outlet />
        </div>

        <div className="px-8 py-3 border-t border-[color:var(--gold-line)] flex items-center justify-between bg-[var(--obsidian-1)]/40">
          <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-ivory-3">
            EIGOO TechOpssa · Vessel I.0 · Audit Cell
          </div>
          <div className="font-serif italic text-[11px] text-ivory-3">
            Vigilance · Discretion · Sovereignty
          </div>
        </div>
      </main>
    </div>
  );
};

export default Layout;
